import React, { useState, useCallback, useMemo, useEffect, useRef } from "react";
import { useNavigate } from "react-router-dom";
import {
  Calendar,
  Loader2,
  ArrowLeft,
  Hotel,
  MapPin,
  BedDouble,
  Users,
  Mail,
  Phone,
  Hash,
  Search,
  CalendarCheck,
  AlertCircle,
  Inbox,
  Ticket
} from "lucide-react";

const API_BASE_URL =
  process.env.REACT_APP_API_BASE_URL || "http://localhost:50574";

const pad = (n) => String(n).padStart(2, "0");
const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const toISODate = (d) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;

const buildDisplay = (y, m, d, hh, mm) => {
  const month = MONTHS[Number(m) - 1];
  if (!month) return null;
  let out = `${pad(Number(d))} ${month} ${y}`;
  if (hh !== undefined && mm !== undefined && !(Number(hh) === 0 && Number(mm) === 0)) {
    const h = Number(hh);
    const suffix = h >= 12 ? "PM" : "AM";
    const h12 = h % 12 === 0 ? 12 : h % 12;
    out += ` · ${pad(h12)}:${pad(Number(mm))} ${suffix}`;
  }
  return out;
};

const formatDisplayDate = (value) => {
  if (value === null || value === undefined || value === "") return "—";
  const str = String(value).trim();

  const dotnet = str.match(/\/Date\((-?\d+)/);
  if (dotnet) {
    const dt = new Date(Number(dotnet[1]));
    if (!Number.isNaN(dt.getTime())) {
      return buildDisplay(dt.getFullYear(), dt.getMonth() + 1, dt.getDate(), dt.getHours(), dt.getMinutes()) || str;
    }
  }

  const iso = str.match(/^(\d{4})-(\d{2})-(\d{2})(?:[T ](\d{2}):(\d{2}))?/);
  if (iso) return buildDisplay(iso[1], iso[2], iso[3], iso[4], iso[5]) || str;

  const dmy = str.match(/^(\d{1,2})[/-](\d{1,2})[/-](\d{4})(?:[ T](\d{1,2}):(\d{2}))?/);
  if (dmy) return buildDisplay(dmy[3], dmy[2], dmy[1], dmy[4], dmy[5]) || str;

  const parsed = new Date(str);
  if (!Number.isNaN(parsed.getTime())) {
    return buildDisplay(parsed.getFullYear(), parsed.getMonth() + 1, parsed.getDate()) || str;
  }
  return str;
};

const getRange = (days) => {
  const end = new Date();
  const start = new Date();
  start.setDate(start.getDate() - days);
  return { from: toISODate(start), to: toISODate(end) };
};

const DEFAULT_DAYS = 30;

const QUICK_RANGES = [
  { label: "Last 30 days", days: 30 },
  { label: "Last 3 months", days: 90 },
  { label: "Last 6 months", days: 180 },
  { label: "Last 1 year", days: 365 },
];

function DateField({ id, label, value, onChange, min, max }) {
  const ref = useRef(null);

  const openPicker = () => {
    try {
      ref.current?.showPicker?.();
    } catch (e) {
   
    }
  };

  return (
    <div className="hb-field">
      <label htmlFor={id}>{label}</label>
      <div className="hb-input" onClick={openPicker}>
        <Calendar size={16} />
        <span className={`hb-input__text${value ? "" : " hb-input__text--empty"}`}>
          {value ? formatDisplayDate(value) : "Select date"}
        </span>
        <input
          ref={ref}
          id={id}
          type="date"
          value={value}
          min={min || undefined}
          max={max || undefined}
          onChange={(e) => onChange(e.target.value)}
          aria-label={label}
        />
      </div>
    </div>
  );
}

export default function HotelBookings({ userSession }) {
  const navigate = useNavigate();

  const userId =
    userSession?.UserId ||
    userSession?.Userkey ||
    userSession?.UserKey ||
    localStorage.getItem("userKey") ||
    "";

  const [fromDate, setFromDate] = useState(() => getRange(DEFAULT_DAYS).from);
  const [toDate, setToDate] = useState(() => getRange(DEFAULT_DAYS).to);
  const [activeDays, setActiveDays] = useState(DEFAULT_DAYS);
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [searched, setSearched] = useState(false);
  const [lastRange, setLastRange] = useState(null);

  const runFetch = useCallback(
    async (from, to) => {
      if (!from || !to) {
        setError("Please select both From Date and To Date.");
        return;
      }
      if (from > to) {
        setError("From Date cannot be greater than To Date.");
        return;
      }
      if (!userId) {
        setError("User ID is missing. Please log in again.");
        return;
      }

      setError("");
      setLoading(true);
      setSearched(true);
      setLastRange({ from, to });

      try {
        const res = await fetch(`${API_BASE_URL}/api/hotel/GetBookingHistory`, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ FromDate: from, ToDate: to, UserId: userId }),
        });

        const json = await res.json();

        if (!res.ok || json.status === "error") {
          setError(json.message || "Failed to fetch booking history.");
          setBookings([]);
          return;
        }

        setBookings(Array.isArray(json.data) ? json.data : []);
      } catch (err) {
        console.error("GetBookingHistory error:", err);
        setError("Network error. Please try again.");
        setBookings([]);
      } finally {
        setLoading(false);
      }
    },
    [userId]
  );

  useEffect(() => {
    runFetch(fromDate, toDate);
   
  }, []);

  const handleQuickRange = (days) => {
    const { from, to } = getRange(days);
    setFromDate(from);
    setToDate(to);
    setActiveDays(days);
    runFetch(from, to);
  };

  const handleFromChange = (v) => {
    setFromDate(v);
    setActiveDays(null);
  };
  const handleToChange = (v) => {
    setToDate(v);
    setActiveDays(null);
  };

const handleViewTicket = (booking) => {
  const bookingId = booking?.TransactionId;
  if (!bookingId) return;

  const from = lastRange?.from || fromDate;
  const to = lastRange?.to || toDate;

navigate(`/user-ticket?bookingId=${bookingId}&fromDate=${from}&toDate=${to}`);
};
  const stats = useMemo(() => {
    const rooms = bookings.reduce((s, b) => s + Number(b.NumberOfRooms || 0), 0);
    const guests = bookings.reduce(
      (s, b) => s + Number(b.NumberOfAdults || 0) + Number(b.NumberOfChild || 0),
      0
    );
    return { total: bookings.length, rooms, guests };
  }, [bookings]);

  return (
    <>
      <HBStyle />
      <div className="hb-page">
      
        <header className="hb-hero">
          <div className="hb-hero__inner">
            <button type="button" className="hb-back" onClick={() => navigate(-1)}>
              <ArrowLeft size={15} /> Back
            </button>
            <div className="hb-hero__row">
              <div className="hb-hero__icon">
                <Hotel size={26} />
              </div>
              <div>
                <h1 className="hb-hero__title">Hotel Booking Summary</h1>
                <p className="hb-hero__sub">
                  Track every stay, guest and reservation in one place.
                </p>
              </div>
            </div>
          </div>
        </header>

        <div className="hb-shell">
         
          <section className="hb-card hb-filters">
            <div className="hb-filters__grid">
              <DateField
                id="hotelFromDate"
                label="From date"
                value={fromDate}
                onChange={handleFromChange}
                max={toDate}
              />
              <DateField
                id="hotelToDate"
                label="To date"
                value={toDate}
                onChange={handleToChange}
                min={fromDate}
              />

              <button
                type="button"
                className="hb-btn"
                onClick={() => runFetch(fromDate, toDate)}
                disabled={loading}
              >
                {loading ? (
                  <>
                    <Loader2 size={16} className="hb-spin" /> Searching…
                  </>
                ) : (
                  <>
                    <Search size={16} /> Find bookings
                  </>
                )}
              </button>
            </div>

            <div className="hb-chips">
              <span className="hb-chips__label">Quick select</span>
              {QUICK_RANGES.map((r) => (
                <button
                  key={r.days}
                  type="button"
                  className={`hb-chip${activeDays === r.days ? " hb-chip--active" : ""}`}
                  onClick={() => handleQuickRange(r.days)}
                  disabled={loading}
                >
                  {r.label}
                </button>
              ))}
            </div>

            {error && (
              <div className="hb-alert" role="alert">
                <AlertCircle size={16} />
                <span>{error}</span>
              </div>
            )}
          </section>

          {/* ---------- Range summary ---------- */}
          {searched && !error && lastRange && (
            <p className="hb-range">
              Showing bookings from <strong>{formatDisplayDate(lastRange.from)}</strong> to{" "}
              <strong>{formatDisplayDate(lastRange.to)}</strong>
            </p>
          )}

          {/* ---------- Stats ---------- */}
          {searched && !loading && !error && bookings.length > 0 && (
            <section className="hb-stats">
              <div className="hb-stat">
                <span className="hb-stat__icon hb-stat__icon--blue">
                  <CalendarCheck size={18} />
                </span>
                <div>
                  <p className="hb-stat__num">{stats.total}</p>
                  <p className="hb-stat__label">Total bookings</p>
                </div>
              </div>
              <div className="hb-stat">
                <span className="hb-stat__icon hb-stat__icon--pink">
                  <BedDouble size={18} />
                </span>
                <div>
                  <p className="hb-stat__num">{stats.rooms}</p>
                  <p className="hb-stat__label">Rooms booked</p>
                </div>
              </div>
              <div className="hb-stat">
                <span className="hb-stat__icon hb-stat__icon--orange">
                  <Users size={18} />
                </span>
                <div>
                  <p className="hb-stat__num">{stats.guests}</p>
                  <p className="hb-stat__label">Total guests</p>
                </div>
              </div>
            </section>
          )}

          {/* ---------- List ---------- */}
          <section className="hb-list">
            {loading ? (
              [0, 1, 2].map((i) => (
                <div className="hb-card hb-skel" key={i}>
                  <div className="hb-skel__avatar" />
                  <div className="hb-skel__lines">
                    <span style={{ width: "46%" }} />
                    <span style={{ width: "70%" }} />
                    <span style={{ width: "32%" }} />
                  </div>
                </div>
              ))
            ) : bookings.length === 0 ? (
              <div className="hb-card hb-empty">
                <div className="hb-empty__icon">
                  <Inbox size={30} />
                </div>
                <h3>{searched && !error ? "No hotel bookings found" : "Find your hotel bookings"}</h3>
                <p>
                  {searched && !error
                    ? "Try a wider date range to see more reservations."
                    : "Pick a date range above, or use a quick select option."}
                </p>
              </div>
            ) : (
              bookings.map((b, idx) => {
                const name = b.HotelName || "Hotel";
                return (
                  <article key={b.TransactionId ?? idx} className="hb-card hb-booking">
                    <div className="hb-booking__top">
                      <div className="hb-avatar">{name.trim().charAt(0).toUpperCase()}</div>

                      <div className="hb-booking__head">
                        <h3 className="hb-booking__title">{name}</h3>
                        <p className="hb-booking__addr">
                          <MapPin size={14} /> {b.HotelAddress || "Address not available"}
                        </p>
                      </div>

                      <span className="hb-badge">
                        <Hash size={12} />
                        {b.TransactionId ?? "—"}
                      </span>
                    </div>

                    <div className="hb-booking__facts">
                      <div className="hb-fact">
                        <BedDouble size={16} />
                        <div>
                          <span className="hb-fact__label">Rooms</span>
                          <strong>{b.NumberOfRooms ?? "—"}</strong>
                        </div>
                      </div>
                      <div className="hb-fact">
                        <Users size={16} />
                        <div>
                          <span className="hb-fact__label">Adults</span>
                          <strong>{b.NumberOfAdults ?? "—"}</strong>
                        </div>
                      </div>
                      <div className="hb-fact">
                        <Users size={16} />
                        <div>
                          <span className="hb-fact__label">Children</span>
                          <strong>{b.NumberOfChild ?? "—"}</strong>
                        </div>
                      </div>
                      <div className="hb-fact">
                        <CalendarCheck size={16} />
                        <div>
                          <span className="hb-fact__label">Booked on</span>
                          <strong>{formatDisplayDate(b.BookingDate)}</strong>
                        </div>
                      </div>
                    </div>

                    <div className="hb-booking__guest">
                      <div className="hb-guest__name">
                        <span className="hb-guest__dot">
                          {(b.CustomerName || "G").trim().charAt(0).toUpperCase()}
                        </span>
                        <div>
                          <span className="hb-fact__label">Guest</span>
                          <strong>{b.CustomerName || "—"}</strong>
                        </div>
                      </div>
                      <span className="hb-guest__contact">
                        <Mail size={14} /> {b.CustomerEmail || "—"}
                      </span>
                      <span className="hb-guest__contact">
                        <Phone size={14} /> {b.CustomerMobile || "—"}
                      </span>

                      <button
                        type="button"
                        className="hb-ticket-btn"
                        onClick={() => handleViewTicket(b)}
                        disabled={!b.TransactionId}
                      >
                        <Ticket size={16} /> View ticket
                      </button>
                    </div>
                  </article>
                );
              })
            )}
          </section>
        </div>
      </div>
    </>
  );
}

// ---------------------------------------------------------------------------
// Styles — scoped under .hb-*
// ---------------------------------------------------------------------------
function HBStyle() {
  return (
    <style>{`
      @import url('https://fonts.googleapis.com/css2?family=Fraunces:opsz,wght@9..144,500;9..144,600&family=Inter:wght@400;500;600;700&display=swap');

      .hb-page, .hb-page *, .hb-page *::before, .hb-page *::after { box-sizing: border-box; }

      .hb-page {
        --ink: #16215C;
        --ink-soft: #26389E;
        --accent: #E4127F;
        --accent-deep: #B80E64;
        --orange: #ea580c;
        --paper: #F4F6FB;
        --card: #FFFFFF;
        --line: #E1E5F2;
        --text: #1A1F36;
        --text-soft: #667085;
        --danger: #B3261E;

        min-height: 100vh;
        background: var(--paper);
        font-family: 'Inter', sans-serif;
        color: var(--text);
        padding-bottom: 64px;
      }

      /* Hero */
      .hb-hero {
        background: linear-gradient(120deg, #0d2b6b 0%, #1a4fa0 55%, #2a63c4 100%);
        color: #fff;
        padding: 28px 20px 84px;
        position: relative;
        overflow: hidden;
      }
      .hb-hero::after {
        content: ""; position: absolute; right: -80px; top: -80px;
        width: 280px; height: 280px; border-radius: 50%;
        background: radial-gradient(circle, rgba(228,18,127,0.35), rgba(228,18,127,0) 70%);
      }
      .hb-hero__inner { max-width: 1080px; margin: 0 auto; position: relative; z-index: 1; }
      .hb-back {
        display: inline-flex; align-items: center; gap: 6px;
        background: rgba(255,255,255,0.14); color: #fff;
        border: 1px solid rgba(255,255,255,0.28);
        border-radius: 999px; padding: 6px 14px;
        font-size: 13px; font-weight: 600; cursor: pointer;
        font-family: inherit; margin-bottom: 20px;
        transition: background 0.15s ease;
      }
      .hb-back:hover { background: rgba(255,255,255,0.26); }
      .hb-hero__row { display: flex; align-items: center; gap: 16px; }
      .hb-hero__icon {
        width: 56px; height: 56px; border-radius: 16px; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: rgba(255,255,255,0.16);
        border: 1px solid rgba(255,255,255,0.28);
      }
      .hb-hero__title {
        font-family: 'Fraunces', serif; font-weight: 600;
        font-size: 28px; margin: 0; line-height: 1.15;
      }
      .hb-hero__sub { margin: 4px 0 0; font-size: 14px; color: rgba(255,255,255,0.82); }

      /* Shell */
      .hb-shell { max-width: 1080px; margin: -56px auto 0; padding: 0 20px; position: relative; z-index: 2; }

      .hb-card {
        background: var(--card);
        border: 1px solid var(--line);
        border-radius: 16px;
        box-shadow: 0 6px 24px rgba(22, 33, 92, 0.07);
      }

      /* Filters */
      .hb-filters { padding: 22px 24px 20px; }
      .hb-filters__grid {
        display: grid; grid-template-columns: 1fr 1fr auto; gap: 16px; align-items: end;
      }
      .hb-field { display: flex; flex-direction: column; gap: 6px; min-width: 0; }
      .hb-field label { font-size: 12px; font-weight: 600; color: var(--text-soft); letter-spacing: 0.3px; text-transform: uppercase; }

      /* Date field: asli input transparent overlay, upar sundar text */
      .hb-input {
        position: relative;
        display: flex; align-items: center; gap: 10px;
        height: 46px;
        border: 1.5px solid var(--line); border-radius: 10px;
        padding: 0 14px; background: #fff; color: var(--ink-soft);
        cursor: pointer;
        transition: border-color 0.15s ease, box-shadow 0.15s ease;
      }
      .hb-input:hover { border-color: #C9D2F0; }
      .hb-input:focus-within { border-color: var(--ink-soft); box-shadow: 0 0 0 4px rgba(38,56,158,0.1); }
      .hb-input__text { font-size: 14px; font-weight: 500; color: var(--text); }
      .hb-input__text--empty { color: #98A2B3; font-weight: 400; }
      .hb-input input {
        position: absolute; inset: 0; width: 100%; height: 100%;
        opacity: 0; cursor: pointer; border: none; font-size: 16px;
      }
      .hb-input input::-webkit-calendar-picker-indicator {
        position: absolute; inset: 0; width: 100%; height: 100%;
        margin: 0; padding: 0; cursor: pointer;
      }

      .hb-btn {
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        height: 46px; padding: 0 26px;
        background: var(--orange); color: #fff;
        border: none; border-radius: 10px;
        font-size: 14px; font-weight: 700; font-family: inherit;
        cursor: pointer; white-space: nowrap;
        box-shadow: 0 6px 16px rgba(234,88,12,0.28);
        transition: background 0.15s ease, transform 0.1s ease;
      }
      .hb-btn:hover { background: var(--accent-deep); }
      .hb-btn:active { transform: translateY(1px); }
      .hb-btn:disabled { opacity: 0.65; cursor: not-allowed; }

      .hb-chips { display: flex; flex-wrap: wrap; align-items: center; gap: 8px; margin-top: 18px; }
      .hb-chips__label { font-size: 12px; color: var(--text-soft); margin-right: 4px; }
      .hb-chip {
        background: #EEF2FF; color: var(--ink-soft);
        border: 1px solid #DCE3FB; border-radius: 999px;
        padding: 6px 14px; font-size: 12.5px; font-weight: 600;
        font-family: inherit; cursor: pointer;
        transition: background 0.15s ease, color 0.15s ease;
      }
      .hb-chip:hover:not(:disabled), .hb-chip--active { background: var(--ink-soft); color: #fff; border-color: var(--ink-soft); }
      .hb-chip:disabled { opacity: 0.6; cursor: not-allowed; }

      .hb-alert {
        display: flex; align-items: center; gap: 8px;
        margin-top: 16px; padding: 10px 14px;
        background: #FEF2F2; border: 1px solid #FECACA; border-radius: 10px;
        color: #B91C1C; font-size: 13.5px; font-weight: 500;
      }
      .hb-alert svg { flex-shrink: 0; }

      .hb-range { margin: 14px 4px 0; font-size: 13px; color: var(--text-soft); }
      .hb-range strong { color: var(--ink); font-weight: 600; }

      /* Stats */
      .hb-stats { display: grid; grid-template-columns: repeat(3, 1fr); gap: 14px; margin-top: 14px; }
      .hb-stat {
        display: flex; align-items: center; gap: 14px;
        background: var(--card); border: 1px solid var(--line); border-radius: 14px;
        padding: 16px 18px;
      }
      .hb-stat__icon {
        width: 42px; height: 42px; border-radius: 12px; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
      }
      .hb-stat__icon--blue { background: #EEF2FF; color: var(--ink-soft); }
      .hb-stat__icon--pink { background: #FDE8F3; color: var(--accent); }
      .hb-stat__icon--orange { background: #FFF1E6; color: var(--orange); }
      .hb-stat__num { margin: 0; font-family: 'Fraunces', serif; font-size: 24px; font-weight: 600; line-height: 1.1; }
      .hb-stat__label { margin: 2px 0 0; font-size: 12px; color: var(--text-soft); }

      /* List */
      .hb-list { margin-top: 18px; display: flex; flex-direction: column; gap: 16px; }

      .hb-booking { padding: 20px 22px; transition: box-shadow 0.2s ease, transform 0.2s ease, border-color 0.2s ease; }
      .hb-booking:hover { box-shadow: 0 12px 32px rgba(22,33,92,0.12); transform: translateY(-2px); border-color: #C9D2F0; }

      .hb-booking__top { display: flex; align-items: flex-start; gap: 14px; }
      .hb-avatar {
        width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: linear-gradient(135deg, var(--ink), var(--ink-soft));
        color: #fff; font-family: 'Fraunces', serif; font-size: 22px; font-weight: 600;
      }
      .hb-booking__head { flex: 1; min-width: 0; }
      .hb-booking__title {
        margin: 0 0 4px; font-family: 'Fraunces', serif;
        font-size: 19px; font-weight: 600; line-height: 1.25; overflow-wrap: anywhere;
      }
      .hb-booking__addr {
        display: flex; align-items: flex-start; gap: 6px; margin: 0;
        font-size: 13px; color: var(--text-soft); line-height: 1.4;
      }
      .hb-booking__addr svg { margin-top: 2px; flex-shrink: 0; color: var(--accent); }

      .hb-badge {
        display: inline-flex; align-items: center; gap: 4px; flex-shrink: 0;
        background: #EEF2FF; color: var(--ink-soft);
        border-radius: 999px; padding: 5px 12px;
        font-size: 12px; font-weight: 700;
      }

      .hb-booking__facts {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 12px;
        margin-top: 18px; padding: 14px 16px;
        background: #F8F9FD; border: 1px dashed var(--line); border-radius: 12px;
      }
      .hb-fact { display: flex; align-items: center; gap: 10px; min-width: 0; }
      .hb-fact > svg { color: var(--ink-soft); flex-shrink: 0; }
      .hb-fact div { display: flex; flex-direction: column; gap: 1px; min-width: 0; }
      .hb-fact__label { font-size: 11px; color: var(--text-soft); }
      .hb-fact strong { font-size: 14px; font-weight: 600; overflow-wrap: anywhere; }

      .hb-booking__guest {
        display: flex; flex-wrap: wrap; align-items: center; gap: 12px 28px;
        margin-top: 16px; padding-top: 16px; border-top: 1px solid var(--line);
      }
      .hb-guest__name { display: flex; align-items: center; gap: 10px; }
      .hb-guest__name div { display: flex; flex-direction: column; gap: 1px; }
      .hb-guest__name strong { font-size: 14px; font-weight: 600; }
      .hb-guest__dot {
        width: 34px; height: 34px; border-radius: 50%; flex-shrink: 0;
        display: flex; align-items: center; justify-content: center;
        background: #FDE8F3; color: var(--accent-deep); font-weight: 700; font-size: 14px;
      }
      .hb-guest__contact {
        display: inline-flex; align-items: center; gap: 7px;
        font-size: 13px; color: var(--text-soft); overflow-wrap: anywhere;
      }
      .hb-guest__contact svg { color: var(--ink-soft); flex-shrink: 0; }

      .hb-ticket-btn {
        margin-left: auto;
        display: inline-flex; align-items: center; justify-content: center; gap: 8px;
        height: 40px; padding: 0 20px;
        background: var(--orange); color: #fff;
        border: none; border-radius: 10px;
        font-size: 13.5px; font-weight: 700; font-family: inherit;
        cursor: pointer; white-space: nowrap;
        box-shadow: 0 6px 16px rgba(234,88,12,0.25);
        transition: background 0.15s ease, transform 0.1s ease;
      }
      .hb-ticket-btn:hover:not(:disabled) { background: var(--accent-deep); }
      .hb-ticket-btn:active:not(:disabled) { transform: translateY(1px); }
      .hb-ticket-btn:disabled { opacity: 0.5; cursor: not-allowed; }

      /* Empty */
      .hb-empty { text-align: center; padding: 56px 24px; }
      .hb-empty__icon {
        width: 68px; height: 68px; border-radius: 50%; margin: 0 auto 16px;
        display: flex; align-items: center; justify-content: center;
        background: #EEF2FF; color: var(--ink-soft);
      }
      .hb-empty h3 { margin: 0 0 6px; font-family: 'Fraunces', serif; font-size: 20px; font-weight: 600; }
      .hb-empty p { margin: 0; font-size: 14px; color: var(--text-soft); }

      /* Skeleton */
      .hb-skel { display: flex; gap: 16px; padding: 22px; align-items: center; }
      .hb-skel__avatar, .hb-skel__lines span {
        background: linear-gradient(90deg, #EEF0F7 25%, #F7F8FC 50%, #EEF0F7 75%);
        background-size: 200% 100%; animation: hb-shimmer 1.3s infinite linear;
      }
      .hb-skel__avatar { width: 52px; height: 52px; border-radius: 14px; flex-shrink: 0; }
      .hb-skel__lines { flex: 1; display: flex; flex-direction: column; gap: 10px; }
      .hb-skel__lines span { height: 12px; border-radius: 6px; display: block; }
      @keyframes hb-shimmer { to { background-position: -200% 0; } }

      .hb-spin { animation: hb-spin 0.8s linear infinite; }
      @keyframes hb-spin { to { transform: rotate(360deg); } }

      /* Responsive */
      @media (max-width: 860px) {
        .hb-filters__grid { grid-template-columns: 1fr 1fr; }
        .hb-btn { grid-column: 1 / -1; width: 100%; }
        .hb-booking__facts { grid-template-columns: repeat(2, 1fr); }
      }
      @media (max-width: 560px) {
        .hb-hero { padding: 20px 14px 76px; }
        .hb-hero__title { font-size: 22px; }
        .hb-hero__icon { width: 46px; height: 46px; }
        .hb-shell { padding: 0 12px; }
        .hb-filters { padding: 18px 16px; }
        .hb-filters__grid { grid-template-columns: 1fr; }
        .hb-stats { grid-template-columns: 1fr; }
        .hb-booking { padding: 16px; }
        .hb-booking__top { flex-wrap: wrap; }
        .hb-avatar { width: 44px; height: 44px; font-size: 18px; }
        .hb-booking__title { font-size: 17px; }
        .hb-badge { order: 3; }
        .hb-booking__guest { flex-direction: column; align-items: flex-start; gap: 10px; }
        .hb-ticket-btn { margin-left: 0; width: 100%; height: 44px; }
      }
    `}</style>
  );
}